import { SharingSettings } from '@/components/integrations/SharingSettings'
import { sharingRequest, useCrmSharing } from '@/services/sharing'
import { businessOptionsChanged } from '@/services/businessOptions'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap'
import type { Core } from '@form-validation/core'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useResultToast } from '@/context/ResultToastContext'
import { createFormValidation, isFormValid } from '@/helpers/formValidationHelper'
import { readSession, writeSession } from '@/helpers/storage'
import { publishSession, readError } from '@/services/apiClient'
import { loadCrmAccount, saveCrmAccount, type CrmAccountInput } from '@/services/accountService'

interface AccountForm {
  name: string
  useGridReferences: boolean
  useDeliveryDay: boolean
  useCallDay: boolean
  emailAddress: string
  phone: string
  address1: string
  address2: string
  address3: string
  city: string
  county: string
  postcode: string
  countryCode: string
  vatRegistered: boolean
  vatNumber: string
  limitedCompany: boolean
  limitedCompanyNumber: string
  paymentTerm: string
}

const emptyForm: AccountForm = {
  name: '',
  useGridReferences: true, useDeliveryDay: true, useCallDay: true,
  emailAddress: '',
  phone: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  county: '',
  postcode: '',
  countryCode: '',
  vatRegistered: false,
  vatNumber: '',
  limitedCompany: false,
  limitedCompanyNumber: '',
  paymentTerm: '0',
}

function blank(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function rememberBusinessName(name: string) {
  const current = readSession()
  if (!current?.businessId) return
  const next = {
    ...current,
    businesses: current.businesses.map((item) =>
      item.businessId === current.businessId ? { ...item, businessName: name } : item,
    ),
  }
  writeSession(next)
  publishSession(next)
}

export function CrmAccountScreen() {
  const sharing=useCrmSharing()
  const notify = useResultToast()
  const formRef = useRef<HTMLFormElement>(null)
  const fvRef = useRef<Core | null>(null)
  const [form, setForm] = useState<AccountForm>(emptyForm)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function patch(next: Partial<AccountForm>) {
    setForm((current) => ({ ...current, ...next }))
  }

  useEffect(() => {
    let active = true
    loadCrmAccount()
      .then((account) => {
        if (!active) return
        setForm({
          name: account.name ?? '',
          useGridReferences: account.useGridReferences ?? true, useDeliveryDay: account.useDeliveryDay ?? true, useCallDay: account.useCallDay ?? true,
          emailAddress: account.emailAddress ?? '',
          phone: account.phone ?? '',
          address1: account.address1 ?? '',
          address2: account.address2 ?? '',
          address3: account.address3 ?? '',
          city: account.city ?? '',
          county: account.county ?? '',
          postcode: account.postcode ?? '',
          countryCode: account.countryCode ?? '',
          vatRegistered: account.vatRegistered,
          vatNumber: account.vatNumber ?? '',
          limitedCompany: account.limitedCompany,
          limitedCompanyNumber: account.limitedCompanyNumber ?? '',
          paymentTerm: String(account.paymentTerm ?? 0),
        })
        setError(null)
      })
      .catch((caught) => {
        if (active) setError(readError(caught))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [])

  useEffect(() => {
    const element = formRef.current
    if (!element || loading) return
    const fv = createFormValidation(element, {
      fields: {
        name: {
          validators: {
            notEmpty: { message: 'Enter the business name' },
            stringLength: { max: 250, message: 'Use 250 characters or fewer' },
          },
        },
        emailAddress: {
          validators: {
            callback: {
              message: 'Enter a valid email address',
              callback: (input: { value?: unknown }) => {
                const value = String(input.value ?? '').trim()
                return value === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
              },
            },
          },
        },
        phone: { validators: { stringLength: { max: 50, message: 'Use 50 characters or fewer' } } },
        address1: { validators: { stringLength: { max: 250, message: 'Use 250 characters or fewer' } } },
        address2: { validators: { stringLength: { max: 250, message: 'Use 250 characters or fewer' } } },
        address3: { validators: { stringLength: { max: 250, message: 'Use 250 characters or fewer' } } },
        city: { validators: { stringLength: { max: 250, message: 'Use 250 characters or fewer' } } },
        county: { validators: { stringLength: { max: 250, message: 'Use 250 characters or fewer' } } },
        postcode: { validators: { stringLength: { max: 10, message: 'Use 10 characters or fewer' } } },
        countryCode: { validators: { stringLength: { max: 3, message: 'Use a 3 character country code' } } },
        vatNumber: {
          validators: {
            notEmpty: { message: 'Enter the VAT number' },
            stringLength: { max: 50, message: 'Use 50 characters or fewer' },
          },
        },
        limitedCompanyNumber: {
          validators: {
            notEmpty: { message: 'Enter the company number' },
            stringLength: { max: 50, message: 'Use 50 characters or fewer' },
          },
        },
        paymentTerm: {
          validators: {
            regexp: { regexp: /^\d{1,4}$/, message: 'Enter the payment term in days' },
          },
        },
      },
    })
    fvRef.current = fv
    return () => { fv.destroy(); fvRef.current = null }
  }, [loading])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!(await isFormValid(fvRef.current))) return
    const input: CrmAccountInput = {
      name: form.name.trim(),
      useGridReferences: form.useGridReferences, useDeliveryDay: form.useDeliveryDay, useCallDay: form.useCallDay,
      emailAddress: blank(form.emailAddress),
      phone: blank(form.phone),
      address1: blank(form.address1),
      address2: blank(form.address2),
      address3: blank(form.address3),
      city: blank(form.city),
      county: blank(form.county),
      postcode: blank(form.postcode),
      countryCode: blank(form.countryCode)?.toUpperCase() ?? null,
      vatRegistered: form.vatRegistered,
      vatNumber: blank(form.vatNumber),
      limitedCompany: form.limitedCompany,
      limitedCompanyNumber: blank(form.limitedCompanyNumber),
      paymentTerm: Number(form.paymentTerm),
    }
    setBusy(true)
    try {
      await saveCrmAccount(input)
      rememberBusinessName(input.name)
      businessOptionsChanged()
      notify(true, 'Business details updated.')
    } catch (caught) {
      notify(false, readError(caught))
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <LoadingSpinner text="Loading account" size="3x" />

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form ref={formRef} noValidate onSubmit={(event) => void submit(event)}>
        <Row className="g-4">
          <Col lg={7}>
            <Card>
              <Card.Header>Business details</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Business name</Form.Label>
                      <Form.Control name="name" value={form.name} maxLength={250} onChange={(event) => patch({ name: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control name="address1" value={form.address1} maxLength={250} onChange={(event) => patch({ address1: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Address line 2</Form.Label>
                      <Form.Control name="address2" value={form.address2} maxLength={250} onChange={(event) => patch({ address2: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Address line 3</Form.Label>
                      <Form.Control name="address3" value={form.address3} maxLength={250} onChange={(event) => patch({ address3: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control name="city" value={form.city} maxLength={250} onChange={(event) => patch({ city: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>County</Form.Label>
                      <Form.Control name="county" value={form.county} maxLength={250} onChange={(event) => patch({ county: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Postcode</Form.Label>
                      <Form.Control name="postcode" value={form.postcode} maxLength={10} onChange={(event) => patch({ postcode: event.target.value })} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control name="emailAddress" type="email" value={form.emailAddress} maxLength={250} onChange={(event) => patch({ emailAddress: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Telephone</Form.Label>
                      <Form.Control name="phone" value={form.phone} maxLength={50} onChange={(event) => patch({ phone: event.target.value })} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Country code</Form.Label>
                      <Form.Control name="countryCode" value={form.countryCode} maxLength={3} onChange={(event) => patch({ countryCode: event.target.value })} />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={5}>
            <Card>
              <Card.Header>Account options</Card.Header>
              <Card.Body>
                <Form.Check
                  id="account-vat-registered"
                  className="mb-3"
                  label="VAT registered"
                  checked={form.vatRegistered}
                  onChange={(event) => patch({ vatRegistered: event.target.checked })}
                />
                <div className={form.vatRegistered ? undefined : 'd-none'}>
                  <Form.Group className="mb-3">
                    <Form.Label>VAT number</Form.Label>
                    <Form.Control name="vatNumber" value={form.vatNumber} maxLength={50} onChange={(event) => patch({ vatNumber: event.target.value })} />
                  </Form.Group>
                </div>
                <Form.Check
                  id="account-limited-company"
                  className="mb-3"
                  label="Limited company"
                  checked={form.limitedCompany}
                  onChange={(event) => patch({ limitedCompany: event.target.checked })}
                />
                <div className={form.limitedCompany ? undefined : 'd-none'}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company number</Form.Label>
                    <Form.Control name="limitedCompanyNumber" value={form.limitedCompanyNumber} maxLength={50} onChange={(event) => patch({ limitedCompanyNumber: event.target.value })} />
                  </Form.Group>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Payment term</Form.Label>
                  <Form.Control name="paymentTerm" inputMode="numeric" value={form.paymentTerm} onChange={(event) => patch({ paymentTerm: event.target.value })} />
                  <Form.Text>Days allowed before payment is due.</Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Card className="mt-4"><Card.Header>Customer options</Card.Header><Card.Body>
          <Form.Check type="switch" id="use-grid-references" label="Use Grid References" checked={form.useGridReferences} onChange={event=>patch({useGridReferences:event.target.checked})} />
          <Form.Check type="switch" id="use-delivery-day" label="Use Delivery Day" checked={form.useDeliveryDay} onChange={event=>patch({useDeliveryDay:event.target.checked})} />
          <Form.Check type="switch" id="use-call-day" label="Use Call day" checked={form.useCallDay} onChange={event=>patch({useCallDay:event.target.checked})} />
          <p className="small text-body-secondary mt-2 mb-0">Turning an option off hides its fields and filters. Existing customer values are retained if you turn it on again.</p>
        </Card.Body></Card>
        <div className="mt-4">
          <Button type="submit" disabled={busy || Boolean(error)}>Save business details</Button>
        </div>
      </Form>
      <SharingSettings request={sharingRequest} appName="CRM" businessKey={sharing.businessKey} actorKey={sharing.actorKey} />
    </>
  )
}
